/**
 * WelcomeModal — shown to first-time users (no Firestore profile document yet).
 *
 * Accessibility (ADR-3, spec §Accessibility):
 *   - role="dialog" aria-modal="true" aria-labelledby
 *   - Focus trapped inside while open
 *   - Closes on Escape key
 *   - Focus returns to the triggering element on close
 *   - No native <dialog> element (for focus-trap compat across browsers)
 *   - Dismissible — never blocks app usage
 *
 * Design: ADR-4 (login-onboarding).
 */

import { useEffect, useRef, useCallback } from "react";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface WelcomeModalProps {
  open: boolean;
  onClose(): void;
  onGoToProfile(): void;
}

// ---------------------------------------------------------------------------
// Focus trap hook
// ---------------------------------------------------------------------------

function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  isActive: boolean,
): void {
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;

    // Focus the first focusable element
    const focusable = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    first?.focus();

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => {
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, [containerRef, isActive]);
}

// ---------------------------------------------------------------------------
// WelcomeModal
// ---------------------------------------------------------------------------

export function WelcomeModal({
  open,
  onClose,
  onGoToProfile,
}: WelcomeModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Capture the triggering element before opening
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement as HTMLElement;
    } else {
      // Return focus to trigger when closed
      triggerRef.current?.focus();
    }
  }, [open]);

  // Focus trap
  useFocusTrap(dialogRef, open);

  // Close on Escape
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  const handleCTA = useCallback(() => {
    onGoToProfile();
    onClose();
  }, [onGoToProfile, onClose]);

  if (!open) return null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      aria-hidden="false"
    >
      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-modal-title"
        onKeyDown={handleKeyDown}
        className="w-full max-w-md rounded-2xl border border-zinc-700 bg-zinc-900 p-6 shadow-2xl"
      >
        <h2
          id="welcome-modal-title"
          className="mb-3 text-xl font-bold text-zinc-100"
        >
          ¡Bienvenido/a a AutoDieta IA!
        </h2>

        <p className="mb-4 text-sm text-zinc-400">
          Para que el agente pueda crear planes personalizados y precisos, necesitamos
          conocer algunos datos de tu salud y hábitos alimentarios.
        </p>

        <p className="mb-6 text-sm text-zinc-400">
          Completar tu perfil te llevará solo unos minutos y mejorará significativamente
          la calidad de las recomendaciones.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] rounded-xl border border-zinc-700 px-6 py-2 text-sm font-medium text-zinc-300 transition-colors hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            Más tarde
          </button>
          <button
            type="button"
            onClick={handleCTA}
            className="min-h-[44px] rounded-xl bg-blue-600 px-6 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            Completar mi perfil
          </button>
        </div>
      </div>
    </div>
  );
}
